/*\
|*|	:: cookies.js ::
|*|	A complete cookies reader/writer framework with full unicode support.
|*|	Revision #7 - September 13th, 2019
|*|	https://developer.mozilla.org/en-US/docs/Web/API/document.cookie
|*|	https://developer.mozilla.org/User:fusionchess
|*|	https://github.com/madmurphy/cookies.js
|*|	This framework is released under the GNU Public License, version 3 or later.
|*|	http://www.gnu.org/licenses/gpl-3.0-standalone.html
|*|	Syntaxes:
|*|	* docCookies.setItem(name, value[, end[, path[, domain[, secure[, same-site]]]]])
|*|	* docCookies.getItem(name)
|*|	* docCookies.removeItem(name[, path[, domain[, secure[, same-site]]]])
|*|	* docCookies.hasItem(name)
|*|	* docCookies.keys()
|*|	* docCookies.clear([path[, domain[, secure[, same-site]]]])
|*|
\*/
const reURIAllowed = /[\-\.\+\*]/g;
const reCNameAllowed = /^(?:expires|max\-age|path|domain|secure|samesite|httponly)$/i;
const makeSetterString = (
	sKey      : string,
	sValue    : string,
	vEnd      : number | string | Date,
	sPath     : string,
	sDomain   : string,
	bSecure   : boolean,
	vSameSite : string | number | boolean
) => {
	var sExpires = '';
	if (vEnd) {
		switch (vEnd.constructor) {
			case Number: {
				/*
				Note: Despite officially defined in RFC 6265, the use of `max-age` is not compatible with any
				version of Internet Explorer, Edge and some mobile browsers. Therefore passing a number to
				the end parameter might not work as expected. A possible solution might be to convert the the
				relative time to an absolute time. For instance, replacing the following line with:
				*/
				/*
				sExpires = vEnd === Infinity ? "; expires=Fri, 31 Dec 9999 23:59:59 GMT" : "; expires=" + (new Date(vEnd * 1e3 + Date.now())).toUTCString();
				*/
				sExpires = (
					(vEnd === Infinity) ? '; expires=Fri, 31 Dec 9999 23:59:59 GMT' :
					'; max-age=' + vEnd
				);
			} break;
			case String: {
				sExpires = '; expires=' + vEnd;
			} break;
			case Date: {
				sExpires = '; expires=' + (<Date>vEnd).toUTCString();
			} break;
		}
	}
	return (
		encodeURIComponent(sKey) +
		'=' +
		encodeURIComponent(sValue) +
		sExpires +
		(sDomain ? '; domain=' + sDomain : '') +
		(sPath   ? '; path='   + sPath   : '') +
		(bSecure ? '; secure'            : '') +
		(
			!vSameSite ||
			(vSameSite.toString().toLowerCase() === 'no_restriction') ||
			(vSameSite < 0 ? '' : (vSameSite.toString().toLowerCase() === 'lax')) ||
			(Math.ceil(<number>vSameSite) === 1) ||
			(vSameSite === true ? '; samesite=lax' : '; samesite=strict')
		)
	);
};
export const getItem = (sKey : string) => {
	if (!sKey) {
		return (null);
	}
	return (
		decodeURIComponent(
			document.cookie.replace(
				new RegExp(
					'(?:(?:^|.*;)\\s*' +
					encodeURIComponent(sKey).replace(reURIAllowed, '\\$&') +
					'\\s*\\=\\s*([^;]*).*$)|^.*$'
				), '$1'
			)
		) || null
	);
};
export const setItem = (
	sKey      : string,
	sValue    : string,
	vEnd      : number | string | Date,
	sPath     : string = '',
	sDomain   : string = '',
	bSecure   : boolean,
	vSameSite : string | number | boolean
) => {
	if (!sKey || reCNameAllowed.test(sKey)) {
		return (false);
	}
	document.cookie = makeSetterString(
		sKey,
		sValue,
		vEnd,
		sPath,
		sDomain,
		bSecure,
		vSameSite
	);
	return (true);
};
export const removeItem = (
	sKey      : string,
	sPath     : string,
	sDomain   : string,
	bSecure   : boolean,
	vSameSite : string | number | boolean
) => {
	if (!hasItem(sKey)) {
		return (false);
	}
	document.cookie = makeSetterString(
		sKey,
		'',
		'Thu, 01 Jan 1970 00:00:00 GMT',
		sPath,
		sDomain,
		bSecure,
		vSameSite
	);
	return (true);
};
export const hasItem = (sKey : string) => {
	if (!sKey || reCNameAllowed.test(sKey)) {
		return (false);
	}
	return (
		(
			new RegExp(
				'(?:^|;\\s*)' +
				encodeURIComponent(sKey).replace(reURIAllowed, '\\$&') +
				'\\s*\\='
			)
		).test(document.cookie)
	);
};
export const keys = () => {
	const aKeys = document.cookie.replace(
		/((?:^|\s*;)[^\=]+)(?=;|$)|^\s*|\s*(?:\=[^;]*)?(?:\1|$)/g, ''
	).split(/\s*(?:\=[^;]*)?;\s*/);
	for (var nLen = aKeys.length, nIdx = 0; nIdx < nLen; nIdx++) {
		aKeys[nIdx] = decodeURIComponent(<string>aKeys[nIdx]);
	}
	return (aKeys);
};
export const clear = (
	sPath     : string,
	sDomain   : string,
	bSecure   : boolean,
	vSameSite : string | number | boolean
) => {
	const aKeys = keys();
	const nLen = aKeys.length;
	for (let nIdx = 0; nIdx < nLen; nIdx++) {
		removeItem(
			<string>aKeys[nIdx],
			sPath,
			sDomain,
			bSecure,
			vSameSite
		);
	}
};
